"use client";

import React, { useEffect, useState, useTransition } from "react";
import { useParams, useRouter, useSearchParams } from "next/navigation";
import { LoadingSpinner } from "@/utils/components/LoadingSpinner";
import { NoDataDisplay } from "@/components/NoDataDisplay";
import { ShareIcon } from "@heroicons/react/24/outline";
import {
  Button,
  CompetitionCard,
  Dropdown,
  MatchCard,
  Tab,
} from "@nrldigitaltechnology/ui-community";
import { CompCardData } from "@nrldigitaltechnology/ui-community/dist/components/CompetitionCard/types";
import {
  MatchCardBtnType,
  MatchCardDataProps,
} from "@nrldigitaltechnology/ui-community/dist/components/MatchCard/types";
import { TabOptionProps } from "@nrldigitaltechnology/ui-community/dist/components/Tab/types";
import { ageLevels, statusMapper } from "@/utils/constants";
import { getS3URL, getTimeRange, getTodaysDate } from "@/utils/utilities";
import { matchesControllerFindAssociationMatchesByDate } from "@/utils/api";
import { useStateRegionStore } from "@/store/store";
import ImageWithFallback from "@/layouts/ImageFallBack";

export function AssociationClient(associationProps: any) {
  const params = useParams();
  const router = useRouter();
  const { association: associationState, setClub: setClubStore, setCompetition } = useStateRegionStore();
  const [isPending, startTransition] = useTransition();
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [isLoadingMatches, setLoadingMatches] = useState<boolean>(false);
  const [competitions, setCompetitions] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [matches, setMatches] = useState([]);

  const [association, setAssociation] = useState<any>(null);

  // Competition Tab Filters
  const [ageLevelFilter, setAgeLevelOptions] = useState<any[]>([]);
  const [seasonFilter, setSeasonOptions] = useState<any[]>([]);
  const [seasonTagFilter, setSeasonTagOptions] = useState<any[]>([]);
  const [selectedAgeLevel, setAgeLevel] = useState<any>(null);
  const [selectedSeason, setSeason] = useState<any>(null);
  const [selectedSeasonTag, setSeasonTag] = useState<any>(null);

  const [clubFilter, setClubOptions] = useState<any[]>([]);
  const [fieldNumberFilter, setFieldNumberOptions] = useState<any[]>([]);
  const [venueFilter, setVenueOptions] = useState<any[]>([]);
  const [selectedClub, setClub] = useState<any>(null);
  const [selectedFieldNumber, setFieldNumber] = useState<any>(null);
  const [selectedVenue, setVenue] = useState<any>(null);
  const [matchDate, setMatchDate] = useState<any>("");

  const [selectedTab, setSelectedTab] = useState({
    value: "competition",
    label: "Competition",
  });

  const searchParams = useSearchParams();


  const searchMatchDate = searchParams.get("matchDate");
  const searchSeason = searchParams.get("season");
  const searchAge = searchParams.get("age");
  const searchSeasonTag = searchParams.get("seasonTag");
  const searchVenue = searchParams.get("venue");


  const dateStr = getTodaysDate();
  let timer: any;

  const [tooltip, setTooltip] = useState({
    showToolTip: false,
    showClickToolTip: false,
  })

  // mounted api calls for competition, club & venue tab
  useEffect(() => {
    if (associationProps?.data?.competitions) {
      const uniqueSeasons = new Set();
      const uniqueAgeLvl = new Set();
      const uniqueSeasonTag = new Set();
      const compCardData = associationProps.data.competitions.map((c: any) => {
        if (c?.orgtree?.association && !association)
          setAssociation(c.orgtree.association);
        if (c.season) uniqueSeasons.add(c.season);
        if (c.ageLvl) uniqueAgeLvl.add(c.ageLvl);
        if (c.seasonTag) uniqueSeasonTag.add(c.seasonTag);

        return {
          _id: c._id,
          name: c.name,
          logo: c.compLogo || getS3URL(c?.orgtree?.association?._id),
          entityLvl1: c?.orgtree?.association?.name,
          entityLvl2: c?.orgtree?.region?.name,
          teams: c.teams.length || 0,
          currentRound: c?.nextMatch?.round?.number,
          currentRoundType: c?.nextMatch?.round?.type,
          upcomingRoundDate: c?.nextMatch?.dateTime,
        };
      });
      const seasonOptions = [...uniqueSeasons]
        .sort((a: any, b: any) => b - a)
        .map((s) => ({ label: s, value: s }));
      const sortedOptions = seasonOptions.sort((a: any, b: any) => a - b);
      setSeasonOptions(sortedOptions);
      if (seasonOptions.length) setSeason(seasonOptions[0]);

      const ageLvlOptions = ageLevels.filter((a) =>
        [...uniqueAgeLvl].includes(a.label),
      );
      setAgeLevelOptions(ageLvlOptions);

      const seasonTagOptions = [...uniqueSeasonTag]
        .sort((a: any, b: any) => a - b)
        .map((s) => ({ label: s, value: s }));
      setSeasonTagOptions(seasonTagOptions);

      setCompetitions(compCardData);
    }
    if (associationProps?.data?.clubs) {
      const clubCardData = associationProps.data.clubs
        .sort((a: any, b: any) => {
          if (a.isActive && !b.isActive) return -1;
          else if (!a.isActive && b.isActive) return 1;
          else return a?.name.localeCompare(b?.name);
        })
        .map((c: any) => {
          if (!association && c?.orgtree?.association)
            setAssociation(c.orgtree.association);
          return {
            _id: c._id,
            name: c.name,
            logo:
              c?.clubLogo ||
              "/assets/logo/noimage.png",
            entityLvl1: c.orgtree.association.name,
            entityLvl2: c.orgtree.region.name,
          };
        });
      setClubs(clubCardData);
    }
    if (associationProps?.data?.matches) {
      const matchCardData = configureMatches(associationProps.data.matches);
      setMatches(matchCardData);
    }
  }, [associationProps]);

  useEffect(() => {
    const updateLoading = associationProps?.data?.isLoading ? true : false;
    setLoading(updateLoading);
  }, [associationProps?.data?.isLoading]);

  useEffect(() => {
    if (matchDate) {
      setLoadingMatches(true);
      const timeRange = getTimeRange(matchDate);
      const associationId = params?.id ? String(params.id) : "";
      matchesControllerFindAssociationMatchesByDate({
        path: { id: associationId },
        query: {
          startDate: `${timeRange[0]}`,
          endDate: `${timeRange[1]}`,
        },
      }).then((v: any) => {
        const updatedMatchList = v.data ? configureMatches(v.data) : [];
        if (v.data) {
          associationProps.data.matches = v.data;
          setMatches(updatedMatchList);
        }
        setLoadingMatches(false);
      });
    }
  }, [matchDate]);

  // Competition Tab Filter Effect
  useEffect(() => {
    const filteredCompetitions = associationProps.data.competitions.filter(
      (c: any) => {
        const ageLvlCheck = selectedAgeLevel?.label
          ? selectedAgeLevel.label === c.ageLvl
          : true;
        const seasonCheck = selectedSeason?.label
          ? selectedSeason.label === c.season
          : true;
        const seasonTagCheck = selectedSeasonTag?.label
          ? selectedSeasonTag.label === c.seasonTag
          : true;
        return seasonCheck && ageLvlCheck && seasonTagCheck;
      },
    );
    const compCardData = filteredCompetitions
      .sort((a: any, b: any) => {
        if (a.isActive && !b.isActive) return -1;
        else if (!a.isActive && b.isActive) return 1;
        else return a?.name.localeCompare(b?.name);
      })
      .map((c: any) => ({
        _id: c._id,
        name: c.name,
        logo: c.compLogo || getS3URL(c?.orgtree?.association?._id),
        entityLvl1: c?.orgtree?.association?.name,
        entityLvl2: c?.orgtree?.region?.name,
        teams: c?.teams.length || 0,
        currentRound: c?.nextMatch?.round?.number,
        currentRoundType: c?.nextMatch?.round?.type.toLowerCase(),
        upcomingRoundDate: c?.nextMatch?.dateTime,
      }));
    setCompetitions(compCardData);
  }, [selectedAgeLevel, selectedSeason, selectedSeasonTag]);

  // Venue Tab Filter Effect
  useEffect(() => {
    const matchList = associationProps?.data?.matches;
    if (!Array.isArray(matchList)) {
      setMatches([]);
      return;
    }

    const validMatches = matchList?.filter((m: any) => m != null);
    const filteredMatches = validMatches?.filter((m: any) => {
      const clubCheck = selectedClub?.value
        ? [
          m?.awayTeam?.orgtree?.club?._id,
          m?.homeTeam?.orgtree?.club?._id,
        ].includes(selectedClub.value)
        : true;
      const fieldNumberCheck = selectedFieldNumber?.label
        ? selectedFieldNumber.label === m?.meta?.fieldNo
        : true;
      const venueCheck = selectedVenue?.value
        ? selectedVenue.value === m?.fullVenue?._id
        : true;
      return clubCheck && fieldNumberCheck && venueCheck;
    });
    const matchCardData = configureMatches(filteredMatches, true);
    setMatches(matchCardData);
  }, [
    selectedClub,
    selectedFieldNumber,
    selectedVenue,
    associationProps?.data?.matches,
  ]);

  const onChangeTab = (item: TabOptionProps) => {
    setSelectedTab(item);
    if (!searchParams?.get('matchDate')) {
      if (item.value === "venue" && dateStr) setMatchDate(dateStr);
    }
  };
  const onDateSelect = (event: any) => {
    clearTimeout(timer);
    timer = setTimeout(() => setMatchDate(event.target.value), 500);
  };
  const handleCompetitionCardClick = (cardData: CompCardData) => {
    setLoadingId(cardData._id?.toString() || null);
    setCompetition({
      label: cardData.name,
      value: cardData._id.toString(),
      logo: cardData.logo,
    })
    const path = `/competitions/${cardData._id}?label=${encodeURIComponent(cardData.name)}&type=competition`;
    startTransition(() => {
      router.push(path);
    });
  };
  const handleClubCardClick = (cardData: CompCardData) => {
    setClubStore({
      label: cardData.name,
      value: cardData._id.toString(),
      logo: cardData.logo,
    });
    setLoadingId(cardData._id?.toString() || null);
    const path = `/competitions/club/${cardData._id}?label=${encodeURIComponent(cardData.name)}&type=club`;
    startTransition(() => {
      router.push(path);
    });
  };
  const configureMatches = (matches: any, skipFilterValueReset?: boolean) => {
    const uniqueClubs = new Set();
    const uniqueFieldNumbers = new Set();
    const uniqueVenues = new Set();

    const configuredMatches = matches
      .sort((a: any, b: any) => {
        if (!a?.dateTime) return 1;
        if (!b?.dateTime) return -1;
        if (a.dateTime === b.dateTime) {
          if (a?.round?.type === "Regular" && b?.round?.type === "Final")
            return -1;
          else if (a?.round?.type === "Final" && b?.round?.type === "Regular")
            return 1;
          else return a?.round?.number - b?.round?.number;
        }
        return a.dateTime - b.dateTime;
      })
      .map((m: any) => {
        // saving unique values for venues tab filter
        if (m.awayTeam) uniqueClubs.add(m.awayTeam?.orgtree?.club?._id);
        if (m.homeTeam) uniqueClubs.add(m.homeTeam?.orgtree?.club?._id);
        if (m?.meta?.fieldNo) uniqueFieldNumbers.add(m.meta.fieldNo);
        if (m?.fullVenue?._id) uniqueVenues.add(m?.fullVenue?._id);

        if (!association && m?.competition?.orgtree?.association)
          setAssociation(m.competition.orgtree.association);

        let matchStatus: { id: string; label: string; shortLabel?: string } = {
          id: "",
          label: "",
        };
        switch (m.status) {
          case "final":
            matchStatus = {
              id: "fulltime",
              label: "FULLTIME",
              shortLabel: "F/T",
            };
            break;
          case "forfeit":
          case "postponed":
          case "washout":
            matchStatus =
              statusMapper.find((s) => s.id === m.status) || matchStatus;
            break;

          default:
            if (m?.meta?.isTba)
              matchStatus =
                statusMapper.find((s) => s.id === "tba") || matchStatus;
            else if (m.status === "pre-game" || m.status === "in-progress") {
              const gameStateForMatch =
                m && m.gameStates && m.gameStates.length ? m.gameStates : [];
              if (gameStateForMatch.length > 0) {
                const lastElement =
                  gameStateForMatch[gameStateForMatch.length - 1] || "";
                if (
                  typeof lastElement === "string" &&
                  (lastElement.toLowerCase().includes("Period") ||
                    lastElement.toLowerCase().includes("First Half") ||
                    lastElement.toLowerCase().includes("Extra Time"))
                )
                  matchStatus =
                    statusMapper.find((s) => s.id === "live") || matchStatus;
              }
            } else {
              matchStatus = { label: "", id: "" };
            }
            break;
        }
        return {
          id: m._id,
          round: `${m?.competition?.name}: ${m?.round?.displayName}`,
          homeTeam: {
            id: m?.homeTeam?._id,
            name: m?.homeTeam?.name,
            score: m?.scores?.homeTeam,
            logo: m?.homeTeam?.avatar
              ? m.homeTeam.avatar
              : m?.meta?.isTba
                ? "/assets/logo/noimage_large.png"
                : getS3URL(association?._id) ??
                getS3URL(m?.homeTeam?.orgtree?.club?._id) ??
                "/assets/logo/noimage_large.png",
            forfeit:
              m.status === "forfeit" &&
              m?.meta?.forfeitingTeam?._id &&
              m.meta.forfeitingTeam._id === m?.homeTeam?._id,
          },
          awayTeam: {
            id: m?.awayTeam?._id,
            name: m?.awayTeam?.name,
            score: m?.scores?.awayTeam,
            logo: m?.awayTeam?.avatar
              ? m.awayTeam.avatar
              : m?.meta?.isTba
                ? "/assets/logo/noimage_large.png"
                : getS3URL(association?._id) ??
                getS3URL(m?.awayTeam?.orgtree?.club?._id) ??
                "/assets/logo/noimage_large.png",
            forfeit:
              m.status === "forfeit" &&
              m?.meta?.forfeitingTeam?._id &&
              m.meta.forfeitingTeam._id === m?.awayTeam?._id,
          },
          status: matchStatus,
          venue: m?.venue?.name
            ? `${m.venue.name}${m?.meta?.fieldNo ? " - " + m.meta.fieldNo : ""}`
            : undefined,
          dateTime: m?.dateTime ? new Date(m.dateTime) : "",
          byes: m?.meta?.isBye,
          period: (!m?.meta?.liveUrl && m?.status) || "",
          customStyles: {
            containerStyle: `flex flex-col ${!m?.meta?.isBye ? "justify-between" : ""
              }`,
            dateContainer: "w-auto",
            logoContainer: "w-auto h-auto sm:w-auto sm:h-auto",
            teamContainer:
              "max-w-none w-auto sm:w-auto md:w-auto lg:w-auto xl:w-auto",
            teamLabels: `text-[10px] sm:text-sm ${!m?.meta?.isBye ? "sm:w-[85px] w-[65px]" : ""
              }`,
            teamLogos:
              "sm:w-[75px] sm:h-[75px] w-[65px] h-[65px] object-contain rounded-md",
            timeLabel: "sm:text-base",
          },
          alternateSmMob: true,
        };
      });
    if (!skipFilterValueReset) {
      const clubOptions = [...uniqueClubs]
        .map((c: any) => {
          const matchingValue = matches.find(
            (m: any) =>
              c === m?.awayTeam?.orgtree?.club?._id ||
              c === m?.homeTeam?.orgtree?.club?._id,
          );
          return matchingValue.awayTeam?.orgtree?.club?._id &&
            matchingValue.awayTeam.orgtree.club._id === c
            ? {
              value: matchingValue.awayTeam.orgtree.club._id,
              label: matchingValue.awayTeam.orgtree.club.name,
            }
            : matchingValue?.homeTeam?.orgtree?.club?._id &&
              matchingValue.homeTeam.orgtree.club._id === c
              ? {
                value: matchingValue?.homeTeam?.orgtree?.club?._id,
                label: matchingValue?.homeTeam?.orgtree?.club?.name,
              }
              : false;
        })
        .filter((c) => !!c);
      setClubOptions(clubOptions);

      const fieldNumberOptions = [...uniqueFieldNumbers]
        .sort((a: any, b: any) => a - b)
        .map((fN) => ({ label: fN, value: fN }));
      setFieldNumberOptions(fieldNumberOptions);

      const venueOptions = [...uniqueVenues].map((v) => {
        const matchingValue = matches.find((m: any) => v === m?.fullVenue?._id);
        return {
          label: matchingValue?.fullVenue?.name,
          value: matchingValue?.fullVenue?._id,
        };
      });
      setVenueOptions(venueOptions);
    }

    return configuredMatches;
  };
  const matchClick = (
    cardData: MatchCardDataProps | undefined,
    cardType: MatchCardBtnType,
  ) => {
    if (!cardData) return;
    const currentMatch = associationProps.data.matches.find(
      (m: any) => m._id === cardData?.id,
    );

    if (cardType === "primary" && cardData?.status?.id === "live") {
      if (currentMatch?.meta?.liveUrl) {
        const { meta } = currentMatch;
        if (meta?.liveUrl) {
          let url = meta.liveUrl;
          if (!/^https?:\/\//i.test(url)) {
            url = `https://${url}`;
          }
          window.open(url, "_blank")?.focus();
        }
      }
    } else {
      if (currentMatch?.fullVenue) {
        const { address } = currentMatch.fullVenue;

        let queryStr = `query=${cardData?.venue?.replace(/\s+/g, "+")}`;
        if (address.formatted)
          queryStr = queryStr + `,${address.formatted.replace(/\s+/g, "+")}`;

        const mapsStr = `https://www.google.com/maps/search/?api=1&${queryStr}`;
        window.open(mapsStr, "_blank")?.focus();
      }
    }
  };

  const handleOnClickShare = async (cardData: MatchCardDataProps) => {
    const currentUrl = `${window.location.origin}/competitions/match/${cardData.id}?type=match`;
    try {
      await navigator.clipboard.writeText(currentUrl);
    } catch (error) {
      console.log(error);
    }
  };

  const onClickCompShare = async () => {
    try {
      const url = new URL(window.location.href);
      const params = url.searchParams;
      params.delete("venue");
      params.delete("fieldNo");
      params.delete("club");
      params.delete("matchDate");

      // setVenue(null);
      // setFieldNumber(null);
      // setClub(null);
      // setMatchDate(null);

      selectedSeason?.value ? params.set("season", selectedSeason?.value || "") : params.delete("season");
      selectedAgeLevel?.value ? params.set("age", selectedAgeLevel?.value || "") : params.delete("age");
      selectedSeasonTag?.value ? params.set("seasonTag", selectedSeasonTag?.value || "") : params.delete("seasonTag");
      // window.history.replaceState({}, "", url.toString());
      await navigator.clipboard.writeText(url.toString());
      setTooltip(prev => ({
        ...prev,
        showToolTip: false,
        showClickToolTip: true
      }));

      setTimeout(() => {
        setTooltip(prev => ({
          ...prev,
          showClickToolTip: false
        }));
      }, 1500);

    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  useEffect(() => {
    if (searchMatchDate)
      setMatchDate(searchMatchDate);
  }, [searchMatchDate]);

  useEffect(() => {
    if (!searchVenue && !searchMatchDate) return

    const incClub = searchParams.get("club");
    const incFieldNo = searchParams.get("fieldNo");
    const incVenue = searchParams.get("venue");

    if (incClub && clubFilter.length > 0) {
      const club = clubFilter.find((c) => c.value === Number(incClub));
      setClub(club);
    }
    if (incVenue && venueFilter.length > 0) {
      const venue = venueFilter.find((v) => Number(v.value) === Number(incVenue));
      setVenue(venue);

    }
    if (incFieldNo && fieldNumberFilter.length > 0) {
      const fieldNo = fieldNumberFilter.find((fn) => fn.value === incFieldNo);
      setFieldNumber(fieldNo);
    }
    if (selectedTab?.value !== "venue") {
      setSelectedTab(
        {
          value: "venue",
          label: "Venue",
        },
      );
    }

  }, [venueFilter, fieldNumberFilter, clubFilter, matchDate, searchVenue]);

  useEffect(() => {

    if (!searchSeason && !searchAge && !searchSeasonTag) return;

    const incSeason = searchParams.get("season");
    const incAge = searchParams.get("age");
    const incSeasonTag = searchParams.get("seasonTag");

    if (incSeason && seasonFilter.length > 0) {
      const seasonItem = seasonFilter.find((c) => c.value === Number(incSeason));
      setSeason(seasonItem);
    }

    if (incAge && ageLevelFilter.length > 0) {

      const ageItem = ageLevelFilter.find((c) => c.value === incAge);
      setAgeLevel(ageItem);
    }
    if (incSeasonTag && seasonTagFilter.length > 0) {
      const seasonTagItem = seasonTagFilter.find((c) => c.value === incSeasonTag);
      setSeasonTag(seasonTagItem);
    }

    if (selectedTab?.value !== "competition") {
      setSelectedTab(
        {
          value: "competition",
          label: "Competition",
        },
      );
    }

  }, [ageLevelFilter, seasonFilter, searchSeason, searchAge]);

  const clearCatch = (val: string) => {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    switch (val) {
      case "venue":
        setVenue(null);
        params.delete("venue");
        break;
      case "fieldNo":
        setFieldNumber(null);
        params.delete("fieldNo");
        break;
      case "club":
        setClub(null);
        params.delete("club");
        break;
      case "season":
        setSeason(null);
        params.delete("season");
        break;
      case "age":
        setAgeLevel(null);
        params.delete("age");
        break;
      case "seasonTag":
        setSeasonTag(null);
        params.delete("seasonTag");
        break;
    }
    window.history.replaceState({}, "", url.toString());
  }

  const onClickVenueShare = async () => {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    params.delete("season");
    params.delete("age");
    params.delete("seasonTag");

    setAgeLevel(null);
    setSeason(null);
    setSeasonTag(null);

    selectedVenue?.value ? params.set("venue", selectedVenue?.value || "") : params.delete("venue");
    selectedFieldNumber?.value ? params.set("fieldNo", selectedFieldNumber?.value || "") : params.delete("fieldNo");
    selectedClub?.value ? params.set("club", selectedClub?.value || "") : params.delete("club");
    matchDate && params.set("matchDate", matchDate || "");
    // window.history.replaceState({}, "", url.toString());
    await navigator.clipboard.writeText(url.toString());

    try {
      // Copy current page URL
      // await navigator.clipboard.writeText(window.location.href);
      setTooltip(prev => ({
        ...prev,
        showToolTip: false,
        showClickToolTip: true
      }));

      setTimeout(() => {
        setTooltip(prev => ({
          ...prev,
          showClickToolTip: false
        }));
      }, 1500);

    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <div className="flex w-full flex-col items-center bg-[#F8F8F8]">
      <div className="w-[90%] max-w-[82.5rem] flex flex-col my-10">
        {associationState?.logo && (
          <div className="flex items-center gap-4">
            <div className="w-[60px] h-[60px] rounded-md bg-white p-[8px]">
              <ImageWithFallback
                src={associationState?.logo}
                alt="Association Logo"
                style="object-contain w-full h-full" fallbackSrc={"/assets/logo/noimage.png"} />
            </div>
            <span className="font-base/[30px] font-medium uppercase tracking-[1.2px]">
              {associationState?.label}
            </span>
          </div>
        )}
        <Tab
          options={[
            {
              value: "competition",
              label: "Competition",
            },
            {
              value: "club",
              label: "Club",
            },
            {
              value: "venue",
              label: "Venue",
            },
          ]}
          tab={selectedTab}
          onChangeTab={onChangeTab}
          variant="underline"
          customStyles={{
            containerStyle:
              "bg-white rounded-t-md border-b border-b-[#D2D2D2] px-[16px] overflow-x-auto overflow-y-clip mt-6",
            tabBtnStyle:
              "data-[selected=true]:border-b-[3px] mb-[-1px] py-[10px] px-[16px]",
          }}
        />

        {selectedTab.value === "competition" &&
          (!isLoading ? (
            Array.isArray(competitions) && competitions.length ? (
              <React.Fragment>
                <div className="flex lg:flex-row flex-col gap-2 w-full bg-white rounded-b-md px-[24px] py-[16px]">
                  {seasonFilter.length ? (
                    <Dropdown
                      placeHolder="Year"
                      clearCb={() => clearCatch("season")}
                      options={seasonFilter}
                      onSelect={(v) => {
                        setSeason(v);
                      }}
                      selectedOption={selectedSeason}
                      customStyles={{
                        containerStyle: "lg:w-[200px]",
                      }}
                    />
                  ) : null}
                  {ageLevelFilter.length ? (
                    <Dropdown
                      placeHolder="Age"
                      clearCb={() => clearCatch("age")}
                      options={ageLevelFilter}
                      onSelect={(v) => setAgeLevel(v)}
                      selectedOption={selectedAgeLevel}
                      customStyles={{
                        containerStyle: "lg:w-[200px]",
                      }}
                    />
                  ) : null}
                  {seasonTagFilter.length ? (
                    <div className="lg:w-[200px]">
                      <Dropdown
                        placeHolder="Season"
                        clearCb={() => clearCatch("seasonTag")}
                        options={seasonTagFilter}
                        onSelect={(v) => setSeasonTag(v)}
                        selectedOption={selectedSeasonTag}
                      />
                    </div>
                  ) : null}
                  <div className="relative">
                    <Button
                      variant="primary"
                      btnType="iconOnly"

                      customStyles={{
                        btnStyle: "p-2",
                      }}
                      onMouseOver={() => setTooltip(prev => ({
                        ...prev,
                        showToolTip: true
                      }))}
                      onMouseLeave={() => setTooltip(prev => ({
                        ...prev,
                        showToolTip: false
                      }))}
                      onClick={() => {
                        onClickCompShare();
                      }}
                      iconLeft={<ShareIcon className="w-5 h-5" color="#000" />}
                    />
                    {
                      tooltip.showToolTip && <div className="opacity-50 absolute flex justify-center top-[40px] w-[80px] text-white text-[11px] right-0 bg-black rounded-md p-1 px-1 z-20">
                        <p className="">Copy Link</p>
                      </div>
                    }
                    {
                      tooltip.showClickToolTip && <div className="opacity-50 absolute flex justify-center top-[40px] w-[80px] text-white text-[11px] right-0 bg-black rounded-md p-1 px-1 z-20">
                        <p className="">Link Copied</p>
                      </div>
                    }
                  </div>
                </div>
                <div data-an-module="association-competitions" className="grid grid-cols-1 gap-4 mt-6 md:grid-cols-2 lg:grid-cols-3">
                  {competitions.map((cardData: CompCardData, index: number) => (
                    <div className="relative" key={`${cardData._id}_${index}`}>
                      {loadingId === cardData._id?.toString() && (
                        <div className="bg-black/50 absolute inset-0 flex items-center justify-center z-10 rounded-md">
                          <LoadingSpinner theme="light" />
                        </div>
                      )}
                      <button
                        data-an-el="association-competition-card"
                        className={`block w-full text-left ${loadingId === cardData._id?.toString()
                          ? "opacity-100"
                          : "opacity-100"
                          }`}
                        onClick={() => handleCompetitionCardClick(cardData)}
                      >
                        <CompetitionCard
                          variant="competition"
                          fallbackImg={"/assets/logo/noimage_large.png"}
                          cardData={cardData}
                          customStyles={{
                            containerStyle: "h-full justify-between",
                            imgStyle: "sm:max-w-[88px] max-w-[68px]",
                          }}
                        />
                      </button>
                    </div>
                  ))}
                </div>
              </React.Fragment>
            ) : (
              <NoDataDisplay dataSearch="competitions" customStyle="mt-6" />
            )
          ) : (
            <LoadingSpinner />
          ))}
        {selectedTab.value === "club" &&
          (!isLoading ? (
            Array.isArray(clubs) && clubs.length ? (
              <div data-an-module="association-clubs" className="grid grid-cols-1 gap-4 mt-6 md:grid-cols-2 lg:grid-cols-3">
                {clubs.map((cardData: CompCardData, index: number) => (
                  <div className="relative" key={`${cardData._id}_${index}`}>
                    {loadingId === cardData._id?.toString() && (
                      <div className="bg-black/50 absolute inset-0 flex items-center justify-center z-10 rounded-md">
                        <LoadingSpinner theme="light" />
                      </div>
                    )}
                    <button
                      data-an-el="association-competition-card"
                      className={`block w-full text-left ${loadingId === cardData._id?.toString()
                        ? "opacity-100"
                        : "opacity-100"
                        }`}
                      onClick={() => handleClubCardClick(cardData)}
                    >
                      <CompetitionCard
                        variant="club"
                        cardData={cardData}
                        fallbackImg={"/assets/logo/nologo.png"}
                        customStyles={{
                          containerStyle: "h-full justify-between",
                          imgStyle: "sm:max-w-[88px] max-w-[68px]",
                        }}
                      />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <NoDataDisplay dataSearch="clubs" customStyle="mt-6" />
            )
          ) : (
            <LoadingSpinner />
          ))}
        {selectedTab.value === "venue" && (
          <React.Fragment>
            <div className="flex lg:flex-row flex-col gap-2 w-full bg-white rounded-b-md px-[24px] py-[16px]">
              {venueFilter.length ? (
                <Dropdown
                  clearCb={() => clearCatch("venue")}
                  placeHolder="Venue"
                  options={venueFilter}
                  onSelect={(v) => setVenue(v)}
                  selectedOption={selectedVenue}
                  customStyles={{
                    containerStyle: "lg:w-[200px]",
                    buttonStyle: "h-11  shadow-sm",
                  }}
                />
              ) : null}
              {fieldNumberFilter.length ? (
                <Dropdown
                  clearCb={() => clearCatch("fieldNo")}
                  placeHolder="Field No"
                  options={fieldNumberFilter}
                  onSelect={(v) => setFieldNumber(v)}
                  selectedOption={selectedFieldNumber}
                  customStyles={{
                    containerStyle: "lg:w-[200px]",
                    buttonStyle: "h-11  shadow-sm",
                  }}
                />
              ) : null}
              {clubFilter.length ? (
                <Dropdown
                  clearCb={() => clearCatch("club")}
                  placeHolder="Club"
                  options={clubFilter}
                  onSelect={(v) => setClub(v)}
                  selectedOption={selectedClub}
                  customStyles={{
                    containerStyle: "lg:w-[200px]",
                    buttonStyle: "h-11  shadow-sm",
                  }}
                />
              ) : null}
              <input
                className="rounded-md border border-[#E2E8F0] h-11 text-md px-[14px] py-[10px] cursor-default bg-white text-left shadow-sm focus:outline-none font-medium sm:text-md sm:leading-6 disabled:border-0 disabled:bg-slate-100 disabled:text-gray-600"
                type="date"
                name="matchDate"
                id="matchDate"
                onChange={onDateSelect}
                defaultValue={matchDate || dateStr}
              />

                  <div className="relative pt-[2px]">
                    <Button
                      variant={matches?.length > 0 ? "primary" : "secondary"}
                      btnType="iconOnly"

                      customStyles={{
                        btnStyle: "p-2",

                      }}
                      onMouseOver={() => setTooltip(prev => ({
                        ...prev,
                        showToolTip: true
                      }))}
                      onMouseLeave={() => setTooltip(prev => ({
                        ...prev,
                        showToolTip: false
                      }))}
                      onClick={() => {
                        matches?.length > 0 &&
                        onClickVenueShare();
                      }}
                      iconLeft={<ShareIcon className="w-6 h-6" color="#000" />}
                    />
                    {
                      tooltip.showToolTip && <div className="opacity-50 absolute flex justify-center top-[40px] w-[80px] text-white text-[11px] right-0 bg-black rounded-md p-1 px-1 z-20">
                        <p className="">Copy Link</p>
                      </div>
                    }
                    {
                      tooltip.showClickToolTip && <div className="opacity-50 absolute flex justify-center top-[40px] w-[80px] text-white text-[11px] right-0 bg-black rounded-md p-1 px-1 z-20">
                        <p className="">Link Copied</p>
                      </div>
                    }
                  </div>
             
            </div>
            {!isLoading && !isLoadingMatches ? (
              Array.isArray(matches) && matches.length ? (
                <div data-an-module="association-matches" className="grid grid-cols-1 gap-4 mt-6 lg:grid-cols-2">
                  {matches.map(
                    (cardData: MatchCardDataProps, index: number) => (
                      <React.Fragment key={`${cardData.id}_${index}`}>
                        <MatchCard
                          shareIconHover={
                            <div className="text-white flex justify-center text-[12px] items-center w-[90px] bg-black/80 rounded-md px-2 py-1">
                              Copy Link
                            </div>
                          }
                          shareIconCopied={
                            <div className="bg-black/70 px-3 py-1 rounded-md font-regular text-white text-sm">
                              Copied to Clipboard
                            </div>
                          }
                          data-an-el="association-match-card"
                          cardData={cardData}
                          onClickCard={() =>
                            router.push(
                              `/competitions/match/${cardData.id}?type=match`,
                            )
                          }
                          onClickVenue={(data, type) => matchClick(data, type)}
                          onClickShare={() => handleOnClickShare(cardData)}
                        />
                      </React.Fragment>
                    ),
                  )}
                </div>
              ) : (
                <NoDataDisplay dataSearch="matches" customStyle="mt-6" />
              )
            ) : (
              <LoadingSpinner />
            )}
          </React.Fragment>
        )}
      </div>
    </div>
  );
}
